import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Tabs,
  Tab,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { isAuthenticated, getCurrentUser, login, register, checkLandlordEmail } from '../../utils/auth';
import { Visibility, VisibilityOff } from '@mui/icons-material';

function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ mt: 3 }}>
      {value === index && children}
    </Box>
  );
}

function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [userType, setUserType] = useState('tenant');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    company: '', // 仅 landlord
    first_name: '',
    last_name: '',
    agreed_to_terms: false,
    agreed_to_privacy: false,
    agreed_to_roommate_terms: false,
  });
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  // 获取用户想要访问的原始页面
  const getRedirectPath = () => {
    // 如果有 from 参数，优先跳转回去
    if (location.state?.from) {
      return location.state.from;
    }
    
    // 否则根据用户类型跳转到对应的个人资料页面
    const user = getCurrentUser();
    if (user?.user_type === 'landlord') {
      return '/landlord/profile';
    } else if (user?.user_type === 'staff' || user?.user_type === 'admin') {
      return '/employee/progress-overview';
    } else {
      return '/profile';
    }
  };

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(getRedirectPath(), { replace: true });
    }
  }, [navigate]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
  };

  const handleUserTypeChange = (event) => {
    setUserType(event.target.value);
    setError('');
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleError = (error) => {
    if (typeof error === 'string') {
      setError(error);
    } else if (typeof error === 'object' && error !== null) {
      const firstKey = Object.keys(error)[0];
      setError(Array.isArray(error[firstKey]) ? error[firstKey][0] : error[firstKey]);
    } else {
      setError('Unknown error');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      if (tabValue === 0) { // Login
        if (formData.email && formData.password) {
          try {
            const result = await login(formData.email, formData.password);
            if (result.success && result.user && result.user.user_type) {
              // 登录成功后跳转回原始页面或默认页面
              navigate(getRedirectPath(), { replace: true });
            } else {
              handleError(result.error);
            }
          } catch (error) {
            handleError('Login failed. Please try again.');
          }
        } else {
          handleError('Please fill in all required fields');
        }
      } else { // Register
        if (userType === 'tenant' && (!formData.email || !formData.password || !formData.first_name || !formData.last_name)) {
          handleError('Please fill in all required fields');
          setLoading(false);
          return;
        }
        if (userType === 'landlord' && (!formData.email || !formData.password || !formData.company)) {
          handleError('Please fill in all required fields');
          setLoading(false);
          return;
        }
        
        // 检查协议同意
        if (!formData.agreed_to_terms || !formData.agreed_to_privacy) {
          handleError('You must agree to Terms of Service and Privacy Policy');
          setLoading(false);
          return;
        }
        if (userType === 'tenant' && !formData.agreed_to_roommate_terms) {
          handleError('You must agree to Roommate Terms of Service');
          setLoading(false);
          return;
        }
        if (userType === 'landlord') {
          await checkLandlordEmailLocal();
        } else {
          try {
            const registerData = {
              email: formData.email,
              password: formData.password,
              user_type: userType,
              company: userType === 'landlord' ? formData.company : '',
              agreed_to_terms: formData.agreed_to_terms,
              agreed_to_privacy: formData.agreed_to_privacy,
              agreed_to_roommate_terms: userType === 'tenant' ? formData.agreed_to_roommate_terms : false,
              ...(userType === 'tenant' ? { 
                first_name: formData.first_name,
                last_name: formData.last_name,
              } : {}),
            };
            const result = await register(registerData);
            if (result.success && result.user && result.user.user_type) {
              // 注册成功后跳转回原始页面或默认页面
              navigate(getRedirectPath(), { replace: true });
            } else {
              handleError(result.error);
            }
          } catch (error) {
            handleError('Registration failed. Please try again.');
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const checkLandlordEmailLocal = async () => {
    if (!formData.email || !formData.company) {
      handleError('Please fill in email and company name');
      setLoading(false);
      return;
    }
    
    try {
      const result = await checkLandlordEmail(formData.email);
      if (result.success && result.isApproved) {
        // 邮箱已批准，继续注册流程
        const registerData = {
          email: formData.email,
          password: formData.password,
          user_type: userType,
          company: formData.company,
          agreed_to_terms: formData.agreed_to_terms,
          agreed_to_privacy: formData.agreed_to_privacy,
          agreed_to_roommate_terms: false, // 房东不需要室友条款
        };
        
        const registerResult = await register(registerData);
        if (registerResult.success && registerResult.user && registerResult.user.user_type) {
          // 注册成功后跳转回原始页面或默认页面
          navigate(getRedirectPath(), { replace: true });
        } else {
          handleError(registerResult.error);
          setLoading(false);
        }
      } else if (result.isServerError) {
        // 服务器错误（503等），显示错误消息而不是对话框
        handleError(result.error || 'Service temporarily unavailable. Please try again later.');
        setLoading(false);
      } else {
        // 邮箱未批准（200响应但is_approved为false），显示对话框
        setOpenDialog(true);
        setLoading(false);
      }
    } catch (error) {
      handleError('Failed to check email. Please try again.');
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Welcome to BunnyRent
        </Typography>
        
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Login" />
          <Tab label="Register" />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <TabPanel value={tabValue} index={0}>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ mt: 2, mb: 2 }}>
              <Link href="/forgot-password" variant="body2">
                Forgot password?
              </Link>
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box component="form" onSubmit={handleSubmit}>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">I want to register as:</FormLabel>
              <RadioGroup
                row
                value={userType}
                onChange={handleUserTypeChange}
              >
                <FormControlLabel 
                  value="tenant" 
                  control={<Radio />} 
                  label="Renter" 
                />
                <FormControlLabel 
                  value="landlord" 
                  control={<Radio />} 
                  label="Landlord" 
                />
              </RadioGroup>
            </FormControl>

            {userType === 'tenant' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                />
              </Box>
            )}
            {userType === 'landlord' && (
              <TextField
                fullWidth
                label="Company Name"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                margin="normal"
                required
              />
            )}
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Terms and Privacy Agreement */}
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Terms and Privacy Agreement *
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <input
                  type="checkbox"
                  checked={formData.agreed_to_terms}
                  onChange={(e) => setFormData(prev => ({ ...prev, agreed_to_terms: e.target.checked }))}
                  required
                  style={{ marginRight: '8px' }}
                />
                <span style={{ marginRight: '4px' }}>*</span>
                <Typography variant="body2">
                  I agree to the{' '}
                  <Link href="#" onClick={(e) => { e.preventDefault(); window.open('/terms?type=terms', '_blank'); }}>
                    Terms of Service
                  </Link>
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <input
                  type="checkbox"
                  checked={formData.agreed_to_privacy}
                  onChange={(e) => setFormData(prev => ({ ...prev, agreed_to_privacy: e.target.checked }))}
                  required
                  style={{ marginRight: '8px' }}
                />
                <span style={{ marginRight: '4px' }}>*</span>
                <Typography variant="body2">
                  I agree to the{' '}
                  <Link href="#" onClick={(e) => { e.preventDefault(); window.open('/terms?type=privacy', '_blank'); }}>
                    Privacy Policy
                  </Link>
                </Typography>
              </Box>
              
              {userType === 'tenant' && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <input
                    type="checkbox"
                    checked={formData.agreed_to_roommate_terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, agreed_to_roommate_terms: e.target.checked }))}
                    required
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ marginRight: '4px' }}>*</span>
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link href="#" onClick={(e) => { e.preventDefault(); window.open('/terms?type=roommate_terms', '_blank'); }}>
                      Roommate Terms of Service
                    </Link>
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Register'}
            </Button>
            {userType === 'landlord' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Note: Landlord registration requires prior approval. 
                We will verify your email address before proceeding.
              </Typography>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* 房东邮箱验证对话框 */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Landlord Registration</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your email is not yet authorized in our system. To register as a landlord, 
            please contact us at <strong>bunnyrent987@gmail.com</strong>. We will verify your information and add your email 
            to our system. After that, you can complete the registration process.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Auth; 